import { useState, useEffect } from 'react';
import useBreakpoint from '../../hooks/useBreakpoint';
import '../style/GuestDashboard.css';
import { Menu, Loader } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { db } from '../../firebase-config.js';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ArrowRight, Info, Share2, Download, Settings, RadioTower, Pen, LucideFileLineChart } from 'lucide-react';
import Notification from '../../notifications/components/Notification.tsx';
import GuestSidebar from '../../guestSidebar/components/GuestSidebar';
import defaultCustomerLogo from "../../media/lisualLogo.jpg";
import { jwtDecode } from "jwt-decode";
import ProjectLastFrameImage from '../../customerDashboard/components/ProjectLastFrameImage.js';


function GuestDashboard() {
  const { customerId } = useParams(); 
  const [projects, setProjects] = useState([]);
  const [customerData, setCustomerData] = useState([]);
  const { isTablet } = useBreakpoint();
  // On tablets the sidebar is an overlay drawer, so start it closed
  const [isSidebarOpen, setIsSidebarOpen] = useState(!isTablet);
  const [activeTab, setActiveTab] = useState("Inicio");
  const [isLoadingContent, setIsLoadingContent] = useState(true);
  const [isLoadingCustomer, setIsLoadingCustomer] = useState(true);
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const [isLoadingLogo, setIsLoadingLogo] = useState(true);
  const [imageOrientations, setImageOrientations] = useState({});
  const [imageLoadingStates, setImageLoadingStates] = useState({});
  const [ftps3ImageUrls, setFtps3ImageUrls] = useState({});
  const [imageRetryCounts, setImageRetryCounts] = useState({});
  const MAX_IMAGE_RETRIES = 2;
  const navigate = useNavigate();
  
  const [projectIdentifierToConfigure, setProjectIdentifierToConfigure] = useState(null);

  const [imageErrorStates, setImageErrorStates] = useState({});
  const [isQRModalOpen, setIsQRModalOpen] = useState(false);
  const [QRscanCustomerId, setQRscanCustomerId] = useState(null);
  const [QRscanIdentifier, setQRscanIdentifier] = useState(null);
  const [userPermissions, setUserPermissions] = useState([]);
  const [userRole, setUserRole] = useState("");






  // === TOKEN DESDE URL (superUser accediendo desde admin) ===
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = searchParams.get("super");

    if (tokenFromUrl) {
      // Guardar token
      localStorage.setItem("authLisualToken", tokenFromUrl);

      // Limpiar la URL (saca el ?token)
      window.history.replaceState({}, document.title, window.location.pathname);

      console.log("🔐 Token superUser guardado desde URL");
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("authLisualToken");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserRole(decoded.role);
        setUserPermissions(decoded.permissions || []);
      } catch (error) {
        console.error("Error decoding token:", error);
      }
    }
  }, []);


  const fetchSingleFtps3ImageUrl = async (projectId, feedID) => {
    const token = localStorage.getItem("authLisualToken");

    try {
      const response = await fetch(
        `https://webback.lisual.com/api/s3-images/${feedID}/latest`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      let imageUrl = null;
      const clonedResponse = response.clone();

      try {
        const data = await response.json();
        imageUrl = data?.url;
      } catch (jsonError) {
        imageUrl = await clonedResponse.text();
      }

      if (imageUrl) {
        setFtps3ImageUrls((prev) => ({
          ...prev,
          [projectId]: imageUrl,
        }));
        setImageLoadingStates((prev) => ({
          ...prev,
          [projectId]: true,
        }));
        return true;
      }
      return false;
    } catch (error) {
      console.error(`Error fetching FTPS3 image URL for ${feedID}:`, error);
      return false;
    }
  };

  const fetchProjects = async () => {
    if (!customerId) return;

    const q = query(collection(db, "usuarios"), where("customerId", "==", customerId));
    try {
      const querySnapshot = await getDocs(q);
      const projectsData = [];
      querySnapshot.forEach((doc) => {
        projectsData.push({ id: doc.id, ...doc.data() });
      });
      setProjects(projectsData);

      const initialLoadingStates = {};
      const newFtps3ImageUrls = {};
      const newRetryCounts = {};
      const newImageErrors = {};

      const token = localStorage.getItem("authLisualToken");

      for (const project of projectsData) {
        initialLoadingStates[project.id] = false;
        newRetryCounts[project.id] = 0;
        newImageErrors[project.id] = false;

        if (!project?.feedID || project?.feedID === 'null') {
          continue;
        }

        initialLoadingStates[project.id] = true;
        try {
          const response = await fetch(
            `https://webback.lisual.com/api/s3-images/${project.feedID}/latest`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }

          let imageUrl = null;
          const clonedResponse = response.clone();

          try {
            const data = await response.json();
            imageUrl = data?.url;
          } catch (jsonError) {
            imageUrl = await clonedResponse.text();
          }

          if (imageUrl) {
            newFtps3ImageUrls[project.id] = imageUrl;
          } else {
            newImageErrors[project.id] = true;
            initialLoadingStates[project.id] = false;
          }
        } catch (err) {
          console.error(`FTPS3 fetch error for ${project.feedID}:`, err);
          newImageErrors[project.id] = true;
          initialLoadingStates[project.id] = false;
        }
      }

      setFtps3ImageUrls(newFtps3ImageUrls);
      setImageRetryCounts(newRetryCounts);
      setImageErrorStates(newImageErrors);
      setImageLoadingStates((prev) => ({
        ...prev,
        ...initialLoadingStates,
      }));

    } catch (error) {
      console.error("Error fetching projects:", error);
    } finally {
      setIsLoadingProjects(false);
    }
  };

  useEffect(() => {
    if (customerId) {
      fetchProjects();
    }
  }, [customerId]); 

  useEffect(() => {
    const fetchCustomerData = async () => {
      if (!customerId) return;
      console.log("here", customerId)

      const q = query(collection(db, "customers"), where("customerId", "==", customerId));
      try {
        const querySnapshot = await getDocs(q);
        const customerData = [];
        querySnapshot.forEach((doc) => {
          customerData.push({ id: doc.id, ...doc.data() });
        });
        console.log("customerDataHere", customerData)
        setCustomerData(customerData);
      } catch (error) {
        console.error("Error fetching customer data:", error);
      } finally {
        setIsLoadingCustomer(false);
      }
    };
    if (customerId) {
      fetchCustomerData();
    }
  }, [customerId]); 

  useEffect(() => {
    if (!isLoadingCustomer && !isLoadingProjects) {
      setIsLoadingContent(false);
    } else {
      setIsLoadingContent(true);
    }
  }, [isLoadingCustomer, isLoadingProjects]);

  useEffect(() => {
    if (!isLoadingCustomer) {
      setIsLoadingLogo(false);
    }
  }, [isLoadingCustomer]);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleSetActiveTab = (tabName) => {
    setActiveTab(tabName);
  };

  const handleIngresar = (identifier) => {
    navigate(`/dashboard/${identifier}`, '_blank');
  };

  

 

  const handleImageLoad = (e, projectId) => {
    const img = e.target;
    const isVertical = img.naturalHeight > img.naturalWidth;
    setImageOrientations(prev => ({
      ...prev,
      [projectId]: isVertical ? 'object-fit-contain' : 'object-fit-cover'
    }));
    setImageLoadingStates(prev => ({
      ...prev,
      [projectId]: false
    }));
    setImageRetryCounts(prev => ({
      ...prev,
      [projectId]: 0
    }));
  };

const handleImageError = async (projectId, project) => {
  const currentRetries = imageRetryCounts[projectId] || 0;

  if (currentRetries < MAX_IMAGE_RETRIES) {
    setImageRetryCounts((prev) => ({
      ...prev,
      [projectId]: currentRetries + 1,
    }));

    const success = await fetchSingleFtps3ImageUrl(projectId, project.feedID);
    if (success) {
      return;
    }
  }

  setImageLoadingStates((prev) => ({
    ...prev,
    [projectId]: false,
  }));
  setImageErrorStates((prev) => ({
    ...prev,
    [projectId]: true,
  }));
};

  useEffect(() => {
    if (customerData.length > 0 && customerData[0].logo) {
      setIsLoadingLogo(false);
    }
  }, [customerData]);


  const [customerAllowed, setCustomerAllowed] = useState(false);

  useEffect(() => {
    const allowedCustomers = ["tecpetrol05122025", "safiar15122025"];
    if (customerId && allowedCustomers.includes(customerId.toLowerCase())) {
      setCustomerAllowed(true);
    } else {
      setCustomerAllowed(false);
    }
  }, [customerId]);




  const [showFunnelUpForm, setShowFunnelUpForm] = useState(false);
  

  return (
    <>



{/* FunnelUp Widget Toggle - Call Button */}
       
      



      <div className="flex h-screen overflow-hidden bg-gray-100">
        <GuestSidebar
          isSidebarOpen={isSidebarOpen}
          toggleSidebar={toggleSidebar}
          activeTab={activeTab}
          setActiveTab={handleSetActiveTab}
          logo={customerData.length > 0 && customerData[0].logo ? customerData[0].logo : defaultCustomerLogo}
          isLoadingLogo={isLoadingLogo}
          customerId={customerId}
          onProjectCreated={fetchProjects}
          customerData={customerData}
        />
        

        <div className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-white border-b border-gray-200 shadow-sm">
          <div className="flex items-center justify-between px-4 py-3">
            {isLoadingLogo ? (
              <p className="text-center text-gray-500">Cargando...</p>
            ) : (
              customerData.length > 0 && customerData[0].logo ? (
                <img src={customerData[0].logo} alt="Logo" className="max-h-12 w-auto" />
              ) : (
                null
              )
            )}
            <button
              onClick={toggleSidebar}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <Menu size={24} className="text-gray-600" />
            </button>
          </div>
        </div>

        <div className={`flex-1 flex flex-col ${isSidebarOpen ? "lg:ml-64" : "lg:ml-0"}`}>
          {/*<FloatingWhatsAppButton message="Tengo una consulta." className="z-40" />*/}

          <main className="flex-1 w-full overflow-y-auto bg-gray-100 p-3 md:p-6 mt-14 lg:mt-0">
            <div className="dashboard-container">

            <div className="mt-16 dashboard-content">
              <Notification />
              </div>

              {isLoadingContent ? (
                <div className="flex justify-center items-center h-full min-h-[300px]">
                  <Loader size={48} className="animate-spin text-blue-500" />
                </div>
              ) : (
                <>
                  <h1>
                    <strong>Bienvenido/a: </strong>{customerData[0]?.name ?? 'Cliente desconocido'}
                  </h1>

                  <br />

                  <div className="project-grid">
                    {projects.filter((project) => {
                      if (userRole === 'guest') {
                        return userPermissions.includes(project.identifier);
                      }
                      return true;
                    })
                    .map((project) => (
                      <div key={project.id} className="project-card">
                        <div className={`project-image-wrapper ${project.feedID ? 'bg-white' : 'bg-white'}`}>
                          {project?.feedID && project?.feedID !== 'null' ? (
                            imageErrorStates[project.id] ? (
                              <div className="flex flex-col items-center justify-center h-full text-center px-4 py-6 text-gray-600 bg-white rounded-md">
                                <p className="text-sm font-medium">
                                  Imagen no disponible
                                </p>
                              </div>
                            ) : (
                              <ProjectLastFrameImage
                                project={project}
                                ftps3ImageUrls={ftps3ImageUrls}
                                imageLoadingStates={imageLoadingStates}
                                imageErrorStates={imageErrorStates}
                                imageOrientations={imageOrientations}
                                handleImageLoad={handleImageLoad}
                                handleImageError={handleImageError}
                                handleIngresar={handleIngresar}
                              />
                            )
                          ) : (
                            <div className="flex flex-col items-center justify-center h-full text-center px-4 py-6 text-gray-600 bg-white rounded-md">
                              <p className="text-sm font-medium">
                                Aguarde que su cámara sea configurada <Settings size={16} className="inline mx-1" />
                              </p>
                              <p className="text-sm font-medium">
                                Luego aguarde la aprobación del técnico.
                              </p>
                            </div>
                          )}
                          <div className="project-title-overlay">
                            {project.Name}
                          </div>
                        </div>
                        <div className="project-button-bar">
                          <button onClick={() => handleIngresar(project.identifier)}>
                            <ArrowRight size={18} />
                          </button>
                          
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </main>
        </div>

        {/*<FloatingWhatsAppButton message="Tengo una consulta." />*/}
        <ToastContainer
          position="top-center"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />





      </div>
    </>
  );
}

export default GuestDashboard;